import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';
import Navbar from '@/pages/home/components/Navbar';
import JsonLd from '@/components/base/JsonLd';
import { articleSchema, breadcrumbSchema } from '@/lib/schema';

export default function ArticleTemplatePage() {
  const navigate = useNavigate();
  const location = useLocation();

  const slug = location.pathname.replace(/^\//, '').replace(/\/$/, '');
  const article = articles.find((a) => a.path === `/${slug}`);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-3">Article Not Found</h1>
          <p className="text-sm text-foreground-500 mb-6">The article you are looking for does not exist.</p>
          <button
            onClick={() => navigate('/')}
            className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Return to Homepage
          </button>
        </div>
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <JsonLd data={articleSchema(article)} />
      <JsonLd data={breadcrumbSchema(article)} />
      <Navbar />
      <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-16">
        <header className="mb-10">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="text-xs font-medium text-primary-600 bg-primary-100 px-2.5 py-1 rounded-full">{article.category}</span>
            <span className="text-xs text-foreground-400">Updated {article.updatedDate}</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-5">
            {article.title}
          </h1>
          <p className="text-base md:text-lg text-foreground-500 leading-relaxed">
            {article.intro}
          </p>
        </header>

        <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-center"
          />
        </div>

        <div className="space-y-10">
          {article.sections.map((section, idx) => (
            <section key={idx} className="mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                {section.heading}
              </h2>
              {section.paragraphs.map((p, pidx) => (
                <p key={pidx} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {p}
                </p>
              ))}
            </section>
          ))}
        </div>

        {relatedArticles.length > 0 && (
          <div className="border-t border-background-200/70 pt-8 mt-10">
            <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">Related Articles</h3>
            <div className="space-y-3">
              {relatedArticles.map((ra) => (
                <button
                  key={ra.slug}
                  onClick={() => navigate(ra.path)}
                  className="flex items-center gap-3 w-full text-left p-3 bg-background-100 rounded-md hover:bg-background-200/70 transition-colors duration-200 cursor-pointer"
                >
                  <i className="ri-file-text-line text-primary-500 flex-shrink-0"></i>
                  <span className="text-sm text-foreground-700">{ra.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-background-200/70 pt-8 mt-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-700 text-sm font-medium rounded-md hover:bg-background-200/70 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Homepage
            </button>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Visit Basetao Spreadsheet Main Site
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </article>
    </>
  );
}